import { Space } from "antd";
import { FC, useEffect, useState } from "react";
import { useLoading } from "../../context/LoadingProvider/useLoading";
import { ActionButtonEnum } from "../../enums/actionButtonEnum";
import { SearchOptionEnum } from "../../enums/searchOptionEnum";
import useActionButtons from "../../hooks/useActionButtons";
import useMonthYear from "../../hooks/useMonthYear";
import useSmartSearchParams, {
  SmartSearchParams,
} from "../../hooks/useSmartSearchParams";
import { Output } from "../../services/types";
import { isString } from "../../utils/stringUtils";
import MonthYear from "../MonthYear";
import Button from "../UI/Button";
import SmartSearch from "../UI/SmartSearch";
import DefaultFormParserLayout from "./default-form-parser-layout";
import { ValidationEnum } from "../../enums/validationEnum";
import { removeEmpty } from "../../utils/objectUtils";
import {SearchStatusEnum} from "../../enums/searchStatusEnum";

export type SmartSearchMonthYearAndActionsLayoutProps = {
  searchBy: SearchOptionEnum[];
  status: string;
  validOnEmpty?: boolean;
  actions: ActionButtonEnum[];
  minYear: number;
  validations?: ValidationEnum[];
  onRefresh: (
    output: Output,
    month: string,
    year: string,
    params: SmartSearchParams
  ) => Promise<string>;
};

const SmartSearchMonthYearAndActionsLayout: FC<
  SmartSearchMonthYearAndActionsLayoutProps
> = ({
  searchBy,
  status,
  validOnEmpty = true,
  actions,
  minYear = 10,
  validations,
  onRefresh,
}) => {
  const loading = useLoading();

  const [report, setReport] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [params, setParams] = useSmartSearchParams();
  const [validSearch, setValidSearch] = useState<boolean>(false);

  const { month, year, years, isMonthEmpty, isYearEmpty, setMonth, setYear } =
    useMonthYear(minYear);

  const {
    isToShowRefreshAction,
    isToShowSaveCSVAction,
    isToShowSavePDFAction,
  } = useActionButtons(actions);

  const disabled =
    (validations?.includes(ValidationEnum.VALIDATE_SMART_SEARCH) &&
      !validSearch) ||
    (validations?.includes(ValidationEnum.VALIDATE_SEARCH) && !search.length) ||
    (validations?.includes(ValidationEnum.VALIDATE_MONTH) && isMonthEmpty) ||
    (validations?.includes(ValidationEnum.VALIDATE_YEAR) && isYearEmpty) ||
    (search.length > 0 && !validSearch);


  const refreshReport = async (output: Output = Output.HTML) => {
    loading.addLoading();

    const res = await onRefresh(output, `${month!}`, year, removeEmpty(params));

    if (Output.isHTML(output) && isString(res)) {
      setReport(res);
    }

    loading.removeLoading();
  };

  return (
    <DefaultFormParserLayout html={report}>
      <Space direction="horizontal" size="middle" wrap>
        <SmartSearch
          autoFocus
          validOnEmpty={validOnEmpty}
          label="Smart Search"
          status={status}
          searchBy={searchBy}
          onChange={(e: string) => setSearch(e)}
          value={search}
          onValidSearch={setValidSearch}
          onObjValueChange={(e: any) => setParams({ ...params, ...e })}
        />

        <MonthYear
          month={month as string}
          year={year}
          years={years}
          onMonthChange={setMonth}
          onYearChange={setYear}
        />

        {isToShowRefreshAction && (
          <Button
            id="report-refresh-button"
            type="primary"
            disabled={disabled}
            onClick={() => refreshReport()}
          >
            Refresh
          </Button>
        )}

        {isToShowSaveCSVAction && (
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => refreshReport(Output.CSV)}
          >
            Save as CSV
          </Button>
        )}

        {isToShowSavePDFAction && (
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => refreshReport(Output.PDF)}
          >
            Save as PDF
          </Button>
        )}
      </Space>
    </DefaultFormParserLayout>
  );
};

export default SmartSearchMonthYearAndActionsLayout;
